import React, { useCallback, useEffect, useState } from 'react';
import { trainingApi } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import SignaturePad from '../SignaturePad';
import { Archive, CheckCheck, FileDown, Loader2, Pencil, PenLine, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import RegisterDialog from './RegisterDialog';
import ModuleFormDialog from './ModuleFormDialog';

const MODE_LABELS = { on_the_job: 'On the job', aula: 'Aula', online: 'Online' };

function SigBlock({ sig, label, onSign, testId }) {
  if (sig?.image) {
    return (
      <div className="text-center" data-testid={`${testId}-done`}>
        <img src={sig.image} alt={label} className="h-10 mx-auto" />
        <p className="text-[10px] text-slate-500">{label}: <b>{sig.name}</b><br />{(sig.at || '').slice(0, 16).replace('T', ' ')}</p>
      </div>
    );
  }
  return (
    <Button variant="outline" size="sm" className="h-8 gap-1.5 rounded-none" onClick={onSign} data-testid={testId}>
      <PenLine className="w-3.5 h-3.5" /> {label}
    </Button>
  );
}

export default function RegisterDetail({ registerId, meta, onClose }) {
  const { user } = useAuth();
  const [reg, setReg] = useState(null);
  const [signCtx, setSignCtx] = useState(null); // {target, target_id, signer, label}
  const [showEdit, setShowEdit] = useState(false);
  const [moduleCtx, setModuleCtx] = useState(null); // {module} — null=chiuso, {module:null}=nuovo
  const [showArchive, setShowArchive] = useState(false);
  const [archComplete, setArchComplete] = useState(true);
  const [busy, setBusy] = useState(false);
  const [daily, setDaily] = useState({ date: new Date().toISOString().slice(0, 10), modules_covered: '', hours: '', mode: 'on_the_job', topics: '' });

  const load = useCallback(() => {
    trainingApi.getRegister(registerId)
      .then(r => setReg(r.data))
      .catch(e => { toast.error(e.response?.data?.detail || 'Errore'); onClose(); });
  }, [registerId, onClose]);

  useEffect(() => { load(); }, [load]);

  const doSign = async (b64) => {
    try {
      await trainingApi.signRegister(registerId, {
        target: signCtx.target, target_id: signCtx.target_id, signer: signCtx.signer, signature_b64: b64,
      });
      toast.success('Firma registrata');
      setSignCtx(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Firma fallita');
    }
  };

  const addDaily = async () => {
    if (!daily.date) { toast.error('Data obbligatoria'); return; }
    setBusy(true);
    try {
      await trainingApi.addDailyLog(registerId, {
        ...daily, hours: daily.hours === '' ? null : parseFloat(daily.hours),
        modules_covered: daily.modules_covered || null, topics: daily.topics || null,
      });
      setDaily(d => ({ ...d, modules_covered: '', hours: '', topics: '' }));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore aggiunta riga');
    } finally {
      setBusy(false);
    }
  };

  const [showPdf, setShowPdf] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const openPdf = async (withReport) => {
    setPdfBusy(true);
    // Apri la scheda SUBITO (sincrono col click): se aperta dopo l'await il
    // popup-blocker la blocca quando la generazione AI richiede più secondi.
    const win = window.open('', '_blank');
    try {
      const r = await trainingApi.registerPdf(registerId, withReport);
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      if (win && !win.closed) win.location = url; else window.open(url, '_blank');
      setShowPdf(false);
    } catch {
      if (win && !win.closed) win.close();
      toast.error(withReport ? 'Generazione report AI non riuscita, riprova' : 'Errore generazione PDF');
    } finally {
      setPdfBusy(false);
    }
  };

  const doArchive = async () => {
    setBusy(true);
    try {
      await trainingApi.archiveRegister(registerId, { complete: archComplete });
      toast.success('PDF archiviato nel profilo staff → cartella Formazione');
      setShowArchive(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Archiviazione fallita');
    } finally {
      setBusy(false);
    }
  };

  const doComplete = async () => {
    if (!window.confirm('Segnare il percorso formativo come concluso?')) return;
    setBusy(true);
    try {
      await trainingApi.updateRegister(registerId, {
        status: 'completato',
        end_date: reg.end_date || new Date().toISOString().slice(0, 10),
      });
      toast.success('Percorso concluso');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Operazione fallita');
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    if (!window.confirm('Eliminare definitivamente questo registro?')) return;
    try {
      await trainingApi.deleteRegister(registerId);
      toast.success('Registro eliminato');
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Eliminazione fallita'); }
  };

  if (!reg) return null;
  const totHours = (reg.daily_log || []).reduce((a, e) => a + (parseFloat(e.hours) || 0), 0);
  const dailyDirty = !!(daily.modules_covered || daily.hours || daily.topics);
  const tryClose = () => {
    if (dailyDirty && !window.confirm(
      'La riga del diario non è stata aggiunta con "Aggiungi": i dati inseriti andranno persi. Chiudere comunque?')) return;
    onClose();
  };

  return (
    <>
      <Dialog open onOpenChange={v => { if (!v && !signCtx) tryClose(); }}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="register-detail"
                       onInteractOutside={e => { if (signCtx) e.preventDefault(); }}
                       onEscapeKeyDown={e => { if (signCtx) e.preventDefault(); }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              Registro formativo — {reg.staff_name}
              <Badge variant="outline" className={reg.status === 'completato'
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}>
                {reg.status === 'completato' ? 'Completato' : 'In corso'}
              </Badge>
              {reg.filed_doc_id && <Badge variant="outline" className="bg-slate-100">Archiviato</Badge>}
              {meta.can_manage && (
                <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-400 hover:text-slate-700"
                        onClick={() => setShowEdit(true)} title="Modifica dati registro (tutor, date, note)"
                        data-testid="register-edit-info-btn">
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
              )}
            </DialogTitle>
            <DialogDescription>
              {reg.store_name || '—'} · Tutor: {reg.tutor_name || '—'} · Inizio: {reg.start_date || '—'}
              {reg.end_date ? ` · Termine: ${reg.end_date}` : ''} · Prossimo controllo: {reg.next_check_date || '—'}
            </DialogDescription>
          </DialogHeader>

          {/* Moduli */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-700">Moduli formativi</h3>
              {meta.can_manage && (
                <Button variant="outline" size="sm" className="h-7 gap-1 rounded-none text-xs"
                        onClick={() => setModuleCtx({ module: null })} data-testid="register-add-module-btn">
                  <Plus className="w-3.5 h-3.5" /> Nuovo modulo
                </Button>
              )}
            </div>
            {(reg.modules || []).length === 0 && (
              <p className="text-sm text-slate-400 border border-dashed border-slate-200 py-4 text-center">Nessun modulo ancora.</p>
            )}
            {(reg.modules || []).map((m, i) => (
              <div key={m.module_id} className="border border-slate-200 p-3" data-testid={`module-card-${i}`}>
                <div className="flex flex-wrap items-center gap-2 mb-1.5">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Modulo {i + 1}{m.module_no ? ` — n° ${m.module_no}` : ''}
                  </p>
                  {(m.types || []).map(t => <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>)}
                  {m.other_type && <span className="text-xs text-slate-500 italic">({m.other_type})</span>}
                  {m.hours != null && <span className="text-xs text-slate-500">{m.hours}h</span>}
                  {meta.can_manage && (
                    <Button variant="outline" size="sm" className="h-6 px-2 gap-1 rounded-none text-[11px] ml-auto"
                            onClick={() => setModuleCtx({ module: m })} data-testid={`module-edit-${i}`}>
                      <Pencil className="w-3 h-3" /> Modifica
                    </Button>
                  )}
                </div>
                {m.area && <p className="text-sm text-slate-700"><b>Area:</b> {m.area}</p>}
                {m.key_actions && <p className="text-sm text-slate-600 whitespace-pre-wrap"><b>Azioni chiave:</b> {m.key_actions}</p>}
                {(m.objectives || []).length > 0 && (
                  <p className="text-sm text-slate-600"><b>Obiettivi:</b> {(m.objectives || []).join(' · ')}</p>
                )}
                {m.next_check_date && <p className="text-xs text-slate-500">Prossima verifica: {m.next_check_date}</p>}
                <div className="flex gap-3 mt-2 pt-2 border-t border-slate-100">
                  <SigBlock sig={m.tutor_signature} label="Firma tutor" testId={`module-${i}-sign-tutor`}
                            onSign={() => setSignCtx({ target: 'module', target_id: m.module_id, signer: 'tutor', label: `Firma tutor — Modulo ${i + 1}` })} />
                  <SigBlock sig={m.trainee_signature} label="Firma allievo" testId={`module-${i}-sign-trainee`}
                            onSign={() => setSignCtx({ target: 'module', target_id: m.module_id, signer: 'trainee', label: `Firma allievo — Modulo ${i + 1}` })} />
                </div>
              </div>
            ))}
          </div>

          {/* Diario giornaliero */}
          <div>
            <div className="flex items-baseline justify-between">
              <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-700">
                Formazione erogata — aggiornamenti giornalieri
              </h3>
              <span className="text-xs text-slate-500">Totale ore: <b>{totHours || 0}</b></span>
            </div>
            <div className="border border-slate-200 mt-2 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="px-2 py-1.5">Giorno</th><th className="px-2 py-1.5">Moduli</th>
                    <th className="px-2 py-1.5">Ore</th><th className="px-2 py-1.5">Modalità</th>
                    <th className="px-2 py-1.5">Argomenti</th><th className="px-2 py-1.5">Firme</th>
                    {meta.can_manage && <th className="px-2 py-1.5" />}
                  </tr>
                </thead>
                <tbody>
                  {(reg.daily_log || []).length === 0 && (
                    <tr><td colSpan={7} className="px-2 py-4 text-center text-slate-400">Nessun aggiornamento giornaliero</td></tr>
                  )}
                  {(reg.daily_log || []).map(e => (
                    <tr key={e.entry_id} className="border-t border-slate-100" data-testid={`daily-row-${e.entry_id}`}>
                      <td className="px-2 py-1.5 whitespace-nowrap">{e.date}</td>
                      <td className="px-2 py-1.5">{e.modules_covered || '—'}</td>
                      <td className="px-2 py-1.5 tabular-nums">{e.hours ?? '—'}</td>
                      <td className="px-2 py-1.5">{MODE_LABELS[e.mode] || '—'}</td>
                      <td className="px-2 py-1.5 max-w-[220px]">{e.topics || '—'}</td>
                      <td className="px-2 py-1.5">
                        <div className="flex gap-1 items-center flex-wrap">
                          {e.tutor_signature?.image
                            ? <img src={e.tutor_signature.image} alt="firma tutor" className="h-6" title={`Tutor: ${e.tutor_signature.name}`} />
                            : <button className="text-[10px] underline text-slate-500" data-testid={`daily-${e.entry_id}-sign-tutor`}
                                      onClick={() => setSignCtx({ target: 'daily', target_id: e.entry_id, signer: 'tutor', label: `Firma tutor — ${e.date}` })}>tutor</button>}
                          {e.trainee_signature?.image
                            ? <img src={e.trainee_signature.image} alt="firma allievo" className="h-6" title={`Allievo: ${e.trainee_signature.name}`} />
                            : <button className="text-[10px] underline text-slate-500" data-testid={`daily-${e.entry_id}-sign-trainee`}
                                      onClick={() => setSignCtx({ target: 'daily', target_id: e.entry_id, signer: 'trainee', label: `Firma allievo — ${e.date}` })}>allievo</button>}
                        </div>
                      </td>
                      {meta.can_manage && (
                        <td className="px-2 py-1.5">
                          <button className="text-red-500" data-testid={`daily-${e.entry_id}-delete`}
                                  onClick={async () => { try { await trainingApi.deleteDailyLog(registerId, e.entry_id); load(); } catch { toast.error('Errore'); } }}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {meta.can_manage && (
              <div className="grid grid-cols-2 md:grid-cols-6 gap-1.5 mt-2 items-end" data-testid="daily-add-form">
                <Input type="date" value={daily.date} onChange={e => setDaily(d => ({ ...d, date: e.target.value }))}
                       className="h-8 rounded-none text-xs" data-testid="daily-date" />
                <Input placeholder="Moduli trattati" value={daily.modules_covered} maxLength={500}
                       onChange={e => setDaily(d => ({ ...d, modules_covered: e.target.value }))}
                       className="h-8 rounded-none text-xs" data-testid="daily-modules" />
                <Input type="number" min="0" step="0.5" placeholder="Ore" value={daily.hours}
                       onChange={e => setDaily(d => ({ ...d, hours: e.target.value }))}
                       className="h-8 rounded-none text-xs" data-testid="daily-hours" />
                <Select value={daily.mode} onValueChange={v => setDaily(d => ({ ...d, mode: v }))}>
                  <SelectTrigger className="h-8 rounded-none text-xs" data-testid="daily-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="on_the_job">On the job</SelectItem>
                    <SelectItem value="aula">Aula</SelectItem>
                    <SelectItem value="online">Online</SelectItem>
                  </SelectContent>
                </Select>
                <Input placeholder="Argomenti trattati" value={daily.topics} maxLength={2000}
                       onChange={e => setDaily(d => ({ ...d, topics: e.target.value }))}
                       className="h-8 rounded-none text-xs" data-testid="daily-topics" />
                <Button size="sm" className="h-8 rounded-none gap-1" onClick={addDaily} disabled={busy} data-testid="daily-add-btn">
                  <Plus className="w-3.5 h-3.5" /> Aggiungi
                </Button>
              </div>
            )}
          </div>

          {reg.notes && <p className="text-sm text-slate-600 border-l-2 border-slate-300 pl-3 whitespace-pre-wrap">{reg.notes}</p>}

          <DialogFooter className="flex-wrap gap-2">
            {user?.role === 'operations_manager' && (
              <Button variant="outline" size="sm" className="h-8 text-red-600 border-red-200 mr-auto" onClick={doDelete} data-testid="register-delete-btn">
                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Elimina
              </Button>
            )}
            {meta.can_manage && reg.status !== 'completato' && (
              <Button variant="outline" size="sm" className="h-8 gap-1.5 text-emerald-700 border-emerald-300"
                      onClick={doComplete} disabled={busy} data-testid="register-complete-btn">
                <CheckCheck className="w-3.5 h-3.5" /> Concludi
              </Button>
            )}
            <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => setShowPdf(true)} data-testid="register-pdf-btn">
              <FileDown className="w-3.5 h-3.5" /> PDF
            </Button>
            {meta.can_manage && (
              <Button size="sm" className="h-8 gap-1.5" onClick={() => setShowArchive(true)} data-testid="register-archive-btn">
                <Archive className="w-3.5 h-3.5" /> Archivia nei documenti
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {signCtx && (
        <SignaturePad title={signCtx.label} subtitle={`${reg.staff_name} — registro formativo`}
                      onSubmit={doSign} onCancel={() => setSignCtx(null)} />
      )}

      {showEdit && (
        <RegisterDialog meta={meta} register={reg}
                        onClose={() => setShowEdit(false)}
                        onSaved={() => { setShowEdit(false); load(); }} />
      )}

      {moduleCtx && (
        <ModuleFormDialog registerId={registerId} register={reg} meta={meta} module={moduleCtx.module}
                          onClose={() => setModuleCtx(null)}
                          onSaved={() => { setModuleCtx(null); load(); }} />
      )}

      {showPdf && (
        <Dialog open onOpenChange={v => { if (!v && !pdfBusy) setShowPdf(false); }}>
          <DialogContent className="max-w-md" data-testid="register-pdf-options-dialog">
            <DialogHeader>
              <DialogTitle>Scarica PDF</DialogTitle>
              <DialogDescription>
                Vuoi includere nel PDF un report che analizza il registro e le formazioni erogate
                (sintesi del percorso, avanzamento, punti di attenzione e suggerimenti generati dall'AI)?
              </DialogDescription>
            </DialogHeader>
            {pdfBusy && (
              <p className="text-sm text-slate-500 flex items-center gap-2" data-testid="register-pdf-generating">
                <Loader2 className="w-4 h-4 animate-spin" /> Generazione in corso… può richiedere qualche secondo.
              </p>
            )}
            <DialogFooter className="flex-wrap gap-2">
              <Button variant="outline" onClick={() => openPdf(false)} disabled={pdfBusy} data-testid="register-pdf-plain-btn">
                Solo registro
              </Button>
              <Button onClick={() => openPdf(true)} disabled={pdfBusy} data-testid="register-pdf-with-report-btn">
                {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null} Registro + report di analisi
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {showArchive && (
        <Dialog open onOpenChange={v => { if (!v) setShowArchive(false); }}>
          <DialogContent className="max-w-md" data-testid="archive-dialog">
            <DialogHeader>
              <DialogTitle>Archivia registro</DialogTitle>
              <DialogDescription>
                Il PDF riepilogativo verrà salvato nel profilo di {reg.staff_name}, cartella "Formazione" della sezione Documenti.
              </DialogDescription>
            </DialogHeader>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={archComplete} onChange={e => setArchComplete(e.target.checked)}
                     data-testid="archive-complete-check" />
              Segna il percorso come completato
            </label>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowArchive(false)}>Annulla</Button>
              <Button onClick={doArchive} disabled={busy} data-testid="archive-confirm-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Archive className="w-4 h-4 mr-1.5" />} Archivia
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
